import { login, logout, loginSMS, loginMFA } from '@/api/user'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { getOrganizationOptions, normalizeOrgListResponse } from '@/api/organization'
import { resetRouter } from '@/router'

const sessionStorageKeys = [
  'client-diagnostics-v1',
  'user_id',
  // Retired numeric role IDs are never restored, but must still be removed
  // when an old browser session signs in or out after the V2 cutover.
  'roles',
  'data_scope',
  'name',
  'orgs',
  'org_id',
  'avatar_url'
]

let organizationSessionEpoch = 0
let organizationRequest = null

function readStoredOrganizations () {
  const stored = sessionStorage.getItem('orgs')
  if (stored === null) return { loaded: false, list: [] }

  try {
    const list = JSON.parse(stored)
    return {
      loaded: true,
      list: normalizeOrgListResponse({ list, total: list.length }).list
    }
  } catch (_) {
    sessionStorage.removeItem('orgs')
    return { loaded: false, list: [] }
  }
}

const getDefaultState = () => {
  const storedOrganizations = readStoredOrganizations()
  return {
    token: getToken() || '',
    user_id: sessionStorage.getItem('user_id') || '',
    data_scope: sessionStorage.getItem('data_scope') || '',
    name: sessionStorage.getItem('name') || '',
    orgs: JSON.stringify(storedOrganizations.list),
    orgs_loaded: storedOrganizations.loaded,
    orgs_status: storedOrganizations.loaded ? 'ready' : 'idle',
    orgs_error: '',
    org_id: sessionStorage.getItem('org_id') || '',
    avatar_url: sessionStorage.getItem('avatar_url') || ''
  }
}

function invalidateOrganizationSession () {
  organizationSessionEpoch += 1
  organizationRequest = null
}

function organizationSessionChangedError () {
  const error = new Error('登录会话已变化，已忽略旧会话的组织列表响应')
  error.name = 'OrganizationSessionChangedError'
  error.isOrganizationSessionStale = true
  return error
}

function organizationErrorMessage (error) {
  if (!error) return '组织筛选项加载失败'
  if (typeof error === 'string') return error
  return error.message || String(error)
}

function clearUserSessionStorage () {
  sessionStorageKeys.forEach(key => sessionStorage.removeItem(key))
}

function positiveIdentityInteger (value) {
  const number = Number(value)
  return Number.isSafeInteger(number) && number > 0 ? number : 0
}

function invalidAccessIdentityError () {
  const error = new Error('IAM V2 access snapshot identity is invalid')
  error.code = 'IAM_V2_ACCESS_INVALID'
  return error
}

const user = {
  state: getDefaultState(),

  mutations: {
    SET_TOKEN: (state, token) => {
      state.token = token
    },
    SET_ID: (state, id) => {
      state.user_id = id
    },
    SET_NAME: (state, name) => {
      state.name = name
    },
    SET_DATA_SCOPE: (state, dataScope) => {
      state.data_scope = dataScope
    },
    SET_ORGS: (state, orgs) => {
      state.orgs = JSON.stringify(orgs)
      state.orgs_loaded = true
      state.orgs_status = 'ready'
      state.orgs_error = ''
    },
    SET_ORGS_LOADING: state => {
      state.orgs_status = 'loading'
      state.orgs_error = ''
    },
    SET_ORGS_ERROR: (state, error) => {
      state.orgs_status = state.orgs_loaded ? 'ready' : 'error'
      state.orgs_error = error
    },
    INVALIDATE_ORGS_CACHE: state => {
      state.orgs = '[]'
      state.orgs_loaded = false
      state.orgs_status = 'idle'
      state.orgs_error = ''
    },
    SET_ORGID: (state, orgId) => {
      state.org_id = orgId
    },
    SET_AVATAR: (state, avatarUrl) => {
      state.avatar_url = avatarUrl
    },
    RESET_USER_STATE: state => {
      Object.assign(state, getDefaultState())
    }
  },

  actions: {
    // 登录
    Login ({ dispatch }, userInfo) {
      // const username = userInfo.username.trim()
      return new Promise((resolve, reject) => {
        login(userInfo).then(response => {
          // The first step of two-factor authentication intentionally has no token.
          if (response.token) {
            dispatch('commitUserInfo', response)
          }
          resolve(response)
        }).catch(error => {
          reject(error)
        })
      })
    },

    // 短信验证码登录
    LoginSMS ({ dispatch }, userInfo) {
      // const username = userInfo.username.trim()
      return new Promise((resolve, reject) => {
        loginSMS(userInfo).then(response => {
          if (!response || !response.token) throw new Error('登录验证未完成，请刷新后重新登录')
          dispatch('commitUserInfo', response)
          resolve(response)
        }).catch(error => {
          reject(error)
        })
      })
    },

    LoginMFA ({ dispatch }, data) {
      return loginMFA(data).then(response => {
        if (!response || !response.token) throw new Error('登录验证未完成，请重新登录')
        return dispatch('commitUserInfo', response).then(() => response)
      })
    },

    commitUserInfo ({commit}, userInfo) {
      // A newly issued token must never inherit the previous session's in-memory
      // identity, IAM V2 snapshot or dynamic routes, even when a user signs in
      // from an already open login tab.
      invalidateOrganizationSession()
      clearUserSessionStorage()
      commit('RESET_USER_STATE')
      commit('access/RESET_ACCESS', null, { root: true })
      commit('permission/RESET_ROUTES', null, { root: true })
      resetRouter()
      setToken(userInfo.token)
      commit('SET_TOKEN', userInfo.token)

      sessionStorage.setItem('user_id', userInfo.user_id)
      commit('SET_ID', userInfo.user_id)

      sessionStorage.setItem('name', userInfo.name)
      commit('SET_NAME', userInfo.name)

      sessionStorage.setItem('data_scope', userInfo.data_scope)
      commit('SET_DATA_SCOPE', userInfo.data_scope)

      sessionStorage.setItem('org_id', userInfo.org_id)
      commit('SET_ORGID', userInfo.org_id)

      sessionStorage.setItem('avatar_url', userInfo.avatar_url)
      commit('SET_AVATAR', userInfo.avatar_url)
    },

    // Cookie credentials are shared across browser tabs, but the historical
    // identity cache is sessionStorage-backed. Recover only the authoritative
    // fields exposed by the authenticated /me/access snapshot and clear every
    // non-authoritative identity fields so a new/account-switched tab
    // cannot mix two users' local state.
    RestoreIdentityFromAccess ({ commit }, subject) {
      const source = subject || {}
      const userID = positiveIdentityInteger(source.user_id)
      const orgID = positiveIdentityInteger(source.org_id)
      const systemID = positiveIdentityInteger(source.system_id)
      const dataScope = positiveIdentityInteger(source.data_scope)
      if (!userID || !orgID || !systemID || dataScope < 1 || dataScope > 4) {
        return Promise.reject(invalidAccessIdentityError())
      }

      invalidateOrganizationSession()
      clearUserSessionStorage()
      commit('RESET_USER_STATE')

      sessionStorage.setItem('user_id', String(userID))
      commit('SET_ID', userID)
      sessionStorage.setItem('data_scope', String(dataScope))
      commit('SET_DATA_SCOPE', dataScope)
      sessionStorage.setItem('org_id', String(orgID))
      commit('SET_ORGID', orgID)
      return Promise.resolve({ user_id: userID, org_id: orgID, system_id: systemID, data_scope: dataScope })
    },

    // 按需加载组织/用户筛选项。同一会话内共享请求与缓存；403/网络错误
    // 只返回给调用页面做局部降级，不会触发主动退出。
    EnsureOrgs ({ commit, state }, options) {
      const force = Boolean(options && options.force)
      const epoch = organizationSessionEpoch

      if (organizationRequest && organizationRequest.epoch === epoch) {
        return organizationRequest.promise
      }

      if (!force && state.orgs_loaded) {
        try {
          const storedList = JSON.parse(state.orgs)
          const list = normalizeOrgListResponse({
            list: storedList,
            total: storedList.length
          }).list
          return Promise.resolve({ list, total: list.length, cached: true })
        } catch (_) {
          sessionStorage.removeItem('orgs')
          commit('INVALIDATE_ORGS_CACHE')
        }
      }

      commit('SET_ORGS_LOADING')
      const requestPromise = getOrganizationOptions().then(response => {
        if (epoch !== organizationSessionEpoch) {
          throw organizationSessionChangedError()
        }
        sessionStorage.setItem('orgs', JSON.stringify(response.list))
        commit('SET_ORGS', response.list)
        return {
          list: response.list,
          total: response.total,
          cached: false
        }
      }).catch(error => {
        if (epoch === organizationSessionEpoch) {
          commit('SET_ORGS_ERROR', organizationErrorMessage(error))
        }
        return Promise.reject(error)
      })

      organizationRequest = { epoch, promise: requestPromise }
      requestPromise.then(() => {
        if (organizationRequest && organizationRequest.promise === requestPromise) {
          organizationRequest = null
        }
      }, () => {
        if (organizationRequest && organizationRequest.promise === requestPromise) {
          organizationRequest = null
        }
      })
      return requestPromise
    },

    // Stage 5/login compatibility alias. Stage 6 pages use EnsureOrgs directly.
    GetOrgs ({ dispatch }, options) {
      return dispatch('EnsureOrgs', options)
    },

    // 登出
    LogOut ({ dispatch, state }) {
      // Capture the credential before clearing local state. Revocation is best effort
      // and must never keep the UI logged in while the network waits or times out.
      logout(state.token || getToken()).catch(() => {})
      return dispatch('ForceLogout')
    },

    // 无论服务端是否可用，都必须完整清理本地身份与动态路由。
    ForceLogout ({ commit }) {
      invalidateOrganizationSession()
      removeToken()
      clearUserSessionStorage()
      commit('RESET_USER_STATE')
      commit('access/RESET_ACCESS', null, { root: true })
      commit('permission/RESET_ROUTES', null, { root: true })
      resetRouter()
      return Promise.resolve()
    },

    FedLogOut ({ dispatch }) {
      return dispatch('ForceLogout')
    }
  }
}

export default user



// WEBPACK FOOTER //
// ./src/store/modules/user.js